import { reactive, computed, ref } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { useToast } from '@/composables/common/useToast';
import { toLocalDateTimeInput, toBackendDateTime, toDateTimeInputValue, formatDateTimeLabel } from '@/utils/dateUtils';

/**
 * useSesionForm — lógica compartida por los modales de crear y editar sesión.
 *
 * Concentra la única parte no trivial del formulario: el contrato de fechas.
 * Las sesiones llegan del listado v2 (`GET /api/v2/events/{evento}/sessions`)
 * con las fechas como instantes ISO 8601 (`start_at`, `end_at`,
 * `sales_close_at`), mientras que los inputs reutilizables (`BaseDateTimeInput`
 * → `<input type="datetime-local">`) trabajan con hora de pared
 * "YYYY-MM-DDTHH:mm". Este composable lee el shape v2, mantiene el estado
 * editable en hora de pared y lo serializa al formato que valida el backend de
 * escritura (v1, sin cambios: `date_format: "Y-m-d H:i:s"`).
 *
 * Modo `crear`: POST /eventos/{evento_id}/sesiones
 * Modo `editar`: PATCH /eventos/{evento_id}/sesiones/{sesion_id}
 *
 * @param {{
 *   sesion?: Object,
 *   evento: Object,
 *   mode?: 'crear'|'editar'
 * }} params
 */
export function useSesionForm({ sesion = {}, evento = {}, mode = 'crear' } = {}) {
    const toast = useToast();
    const loading = ref(false);

    // Estado editable, en hora de pared (lo que ve y escribe el usuario).
    // Lectura del shape v2 del listado: start_at / end_at / doors_open_at /
    // provisional / sales_close_at.
    const form = reactive({
        text: sesion.text != null ? sesion.text : '',
        apertura_puertas: sesion.doors_open_at != null ? sesion.doors_open_at : '',
        fecha_provisional: !!sesion.provisional,
        fecha_inicio: toLocalDateTimeInput(sesion.start_at || ''),
        fecha_fin: toLocalDateTimeInput(sesion.end_at || ''),
        fecha_cierre_venta: toLocalDateTimeInput(sesion.sales_close_at || ''),
    });

    // Rango del evento normalizado a "YYYY-MM-DDTHH:mm", que es lo que
    // consumen los pickers. Un evento de un día guardado a las 00:00
    // (inicio == fin) extiende el máximo al final de ese día: con un rango de
    // ancho cero el calendario no dejaría seleccionar ninguna fecha. Un fin
    // ANTERIOR al inicio es dato incorrecto: no se tapa, se deja el rango
    // imposible para que el picker se deshabilite y el formulario lo explique.
    const rangoEvento = computed(() => {
        const start = toDateTimeInputValue(evento.start_event_date);
        const end = toDateTimeInputValue(evento.end_event_date);

        if (!start) return { min: null, max: end || null, invalido: false };
        if (!end) return { min: start, max: null, invalido: false };
        if (end === start) return { min: start, max: `${start.slice(0, 10)}T23:59`, invalido: false };
        if (end < start) return { min: start, max: end, invalido: true };

        return { min: start, max: end, invalido: false };
    });

    /**
     * Aviso en lenguaje llano para el usuario final: qué pasa y qué revisar.
     * Sólo aplica al caso de fechas invertidas del evento.
     */
    const avisoRangoFechas = computed(() =>
        rangoEvento.value.invalido
            ? 'La fecha de fin del evento es anterior a la fecha de inicio. Revisa las fechas del evento.'
            : ''
    );

    // Límites de la fecha de inicio: el rango del evento.
    const minFechaInicio = computed(() => rangoEvento.value.min);
    const maxFechaInicio = computed(() => rangoEvento.value.max);

    // Límites de la fecha de fin. En eventos de calendario, la sesión debe
    // caber en el mismo día que su inicio (00:00–23:59); en el resto, no puede
    // terminar antes de empezar (y nunca fuera del rango del evento).
    const sameDayBounds = computed(() => {
        if (!evento.calendario || !form.fecha_inicio) return null;
        const [datePart] = form.fecha_inicio.split('T');
        if (!datePart) return null;
        return { min: `${datePart}T00:00`, max: `${datePart}T23:59` };
    });
    const minFechaFin = computed(() => {
        if (evento.calendario) return sameDayBounds.value?.min ?? null;
        return form.fecha_inicio || minFechaInicio.value;
    });
    const maxFechaFin = computed(() =>
        evento.calendario ? (sameDayBounds.value?.max ?? null) : maxFechaInicio.value
    );

    /**
     * Ayuda visible con el rango permitido: explica por qué el calendario
     * deshabilita días, en vez de dejar un control que parece no responder.
     */
    const rangoSesion = computed(() => {
        if (rangoEvento.value.invalido) return '';

        const { min, max } = rangoEvento.value;

        if (min && max) {
            return `La sesión debe estar entre el ${formatDateTimeLabel(min)} y el ${formatDateTimeLabel(max)}.`;
        }

        if (min) return `La sesión no puede ser anterior al ${formatDateTimeLabel(min)}.`;
        if (max) return `La sesión no puede ser posterior al ${formatDateTimeLabel(max)}.`;

        return '';
    });

    // Flags de presentación que dependen de configuración del teatro / manager.
    const puedeEditarCierreVenta = computed(
        () => !!store.getters.getTeatro?.puede_editar_fecha_cierre_venta
    );
    const mostrarFechaProvisional = computed(
        () => store.getters.getFooter?.tipo == 1
    );

    /**
     * Serializa el estado editable al payload que espera el backend.
     * Incluye `evento_id` (merge tag de la URL) y, en edición, `sesion_id`.
     */
    function toPayload() {
        const payload = {
            filters: {},
            evento_id: evento.id,
            fecha: toBackendDateTime(form.fecha_inicio),
            end_session_date: toBackendDateTime(form.fecha_fin),
            text: form.text,
            apertura_puertas: form.apertura_puertas,
            fecha_provisional: form.fecha_provisional,
        };

        if (mode === 'editar') {
            payload.sesion_id = sesion.id;
        }

        if (form.fecha_cierre_venta) {
            payload.fecha_cierre_venta = toBackendDateTime(form.fecha_cierre_venta);
        }

        return payload;
    }

    /**
     * Envía el formulario. Devuelve true si el backend confirmó la operación.
     */
    async function submitSesion() {
        loading.value = true;

        const alias = mode === 'editar'
            ? 'back.events.sesiones.editar'
            : 'back.events.sesiones.crear';

        try {
            const res = await store.dispatch(config.getVuexAlias(alias), toPayload());
            toast.success(res.data.message);
            return true;
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo guardar la sesión');
            return false;
        } finally {
            loading.value = false;
        }
    }

    return {
        form,
        loading,
        minFechaInicio,
        maxFechaInicio,
        minFechaFin,
        maxFechaFin,
        avisoRangoFechas,
        rangoSesion,
        puedeEditarCierreVenta,
        mostrarFechaProvisional,
        toPayload,
        submitSesion,
    };
}
